import React, { useState, useEffect } from "react";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import ssLogo from "../../../assets/images/sslogo.png";
import axios from "axios";
import io from "socket.io-client";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";
import {
  FiPlus,
  FiCalendar,
  FiSave,
  FiDownload,
  FiRefreshCw,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiSliders,
  FiBarChart2,
  FiSearch,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const fallbackChartData = [
  { date: "Jul 15", "916 Gold": 9100, "22K Gold": 8500, "18K Gold": 6980, Silver: 1920, Platinum: 4900 },
  { date: "Jul 16", "916 Gold": 9200, "22K Gold": 8600, "18K Gold": 7050, Silver: 1930, Platinum: 4950 },
  { date: "Jul 17", "916 Gold": 9350, "22K Gold": 8700, "18K Gold": 7120, Silver: 1940, Platinum: 4980 },
  { date: "Jul 18", "916 Gold": 9500, "22K Gold": 8850, "18K Gold": 7250, Silver: 1960, Platinum: 5050 },
  { date: "Jul 20", "916 Gold": 9800, "22K Gold": 9100, "18K Gold": 7450, Silver: 1980, Platinum: 5150 },
  { date: "Jul 21", "916 Gold": 10000, "22K Gold": 9300, "18K Gold": 7600, Silver: 2000, Platinum: 5200 },
];

const getCurrentDateStr = () => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const RateSettings = ({ onClose, zIndex }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);

  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const getClampedToday = () => {
    const todayStr = getCurrentDateStr();
    if (minDate && todayStr < minDate) return minDate;
    if (maxDate && todayStr > maxDate) return maxDate;
    return todayStr;
  };

  const [effectiveDate, setEffectiveDate] = useState(getClampedToday());
  const [activeTab, setActiveTab] = useState("history"); // "history" | "trend"
  const [chartType, setChartType] = useState("bar"); // "bar" | "line"
  const [selectedMonth, setSelectedMonth] = useState("Jul 2026");
  const [historySearchQuery, setHistorySearchQuery] = useState("");
  const [rechartsData, setRechartsData] = useState(fallbackChartData);

  // Rate Classes state
  const [rateClasses, setRateClasses] = useState([
    {
      id: 1,
      name: "916 Gold",
      unit: "per gram",
      badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
      dotColor: "#d97706",
      currentRate: 10000,
      newRate: "10000",
    },
    {
      id: 2,
      name: "22K Gold",
      unit: "per gram",
      badgeColor: "bg-orange-100 text-orange-800 border-orange-300",
      dotColor: "#ea580c",
      currentRate: 9300,
      newRate: "9300",
    },
    {
      id: 3,
      name: "18K Gold",
      unit: "per gram",
      badgeColor: "bg-amber-900/10 text-amber-900 border-amber-400",
      dotColor: "#b45309",
      currentRate: 7600,
      newRate: "7600",
    },
    {
      id: 4,
      name: "Silver",
      unit: "per gram",
      badgeColor: "bg-slate-100 text-slate-700 border-slate-300",
      dotColor: "#64748b",
      currentRate: 2000,
      newRate: "2000",
    },
    {
      id: 5,
      name: "Platinum",
      unit: "per gram",
      badgeColor: "bg-purple-100 text-purple-800 border-purple-300",
      dotColor: "#a855f7",
      currentRate: 5200,
      newRate: "5200",
    },
  ]);

  // Rate History table data
  const [historyData, setHistoryData] = useState([
    {
      date: "21/Jul/2026",
      gold916: { val: "10,000", diff: "+200", isUp: true },
      gold22k: { val: "9,300", diff: "+200", isUp: true },
      gold18k: { val: "7,600", diff: "+150", isUp: true },
      silver: { val: "2,000", diff: "+20", isUp: true },
      platinum: { val: "5,200", diff: "+50", isUp: true },
    },
    {
      date: "20/Jul/2026",
      gold916: { val: "9,800", diff: "+100", isUp: true },
      gold22k: { val: "9,100", diff: "+100", isUp: true },
      gold18k: { val: "7,450", diff: "+70", isUp: true },
      silver: { val: "1,980", diff: "+10", isUp: true },
      platinum: { val: "5,150", diff: "+50", isUp: true },
    },
  ]);

  // Load registered rate classes from database
  const fetchRateClasses = async () => {
    try {
      // 1. Try to fetch rate classes with latest stored rate from rate_history
      const res = await axios.get(`${apiBaseUrl}/main/getRateClassesWithLatestRates/${dbCode}`);
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        const mapped = res.data.map((item, idx) => ({
          id: item.id,
          name: item.rateClass,
          touchPercent: item.touchPercent,
          unit: "per gram",
          badgeColor:
            idx % 5 === 0 ? "bg-amber-100 text-amber-800 border-amber-300" :
            idx % 5 === 1 ? "bg-orange-100 text-orange-800 border-orange-300" :
            idx % 5 === 2 ? "bg-amber-900/10 text-amber-900 border-amber-400" :
            idx % 5 === 3 ? "bg-slate-100 text-slate-700 border-slate-300" :
            "bg-purple-100 text-purple-800 border-purple-300",
          dotColor:
            idx % 5 === 0 ? "#d97706" :
            idx % 5 === 1 ? "#ea580c" :
            idx % 5 === 2 ? "#b45309" :
            idx % 5 === 3 ? "#64748b" :
            "#a855f7",
          currentRate: item.currentRate ? parseFloat(item.currentRate) : 0,
          newRate: item.currentRate ? String(item.currentRate) : "",
        }));
        setRateClasses(mapped);
      } else {
        // Fallback to selectall master rate classes
        const selectRes = await axios.get(`${apiBaseUrl}/main/selectall/${dbCode}`);
        if (selectRes.data && Array.isArray(selectRes.data)) {
          const mapped = selectRes.data.map((item, idx) => ({
            id: item.id,
            name: item.rateClass,
            touchPercent: item.touchPercent,
            unit: "per gram",
            badgeColor:
              idx % 5 === 0 ? "bg-amber-100 text-amber-800 border-amber-300" :
              idx % 5 === 1 ? "bg-orange-100 text-orange-800 border-orange-300" :
              idx % 5 === 2 ? "bg-amber-900/10 text-amber-900 border-amber-400" :
              idx % 5 === 3 ? "bg-slate-100 text-slate-700 border-slate-300" :
              "bg-purple-100 text-purple-800 border-purple-300",
            dotColor:
              idx % 5 === 0 ? "#d97706" :
              idx % 5 === 1 ? "#ea580c" :
              idx % 5 === 2 ? "#b45309" :
              idx % 5 === 3 ? "#64748b" :
              "#a855f7",
            currentRate: item.currentRate ? parseFloat(item.currentRate) : 0,
            newRate: item.currentRate ? String(item.currentRate) : "",
          }));
          setRateClasses(mapped);
        }
      }
    } catch (err) {
      console.warn("Could not fetch rate classes from server, using existing state", err);
    }
  };

  // Load rate history records
  const fetchRateHistory = async () => {
    try {
      const res = await axios.get(`${apiBaseUrl}/main/getRateHistory/${dbCode}`);
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        // Group by date & time
        const grouped = {};
        res.data.forEach((item) => {
          const key = `${item.effectiveDate} ${item.effectiveTime || ''}`.trim();
          if (!grouped[key]) {
            grouped[key] = {
              date: item.effectiveDate,
              time: item.effectiveTime,
              rates: {},
            };
          }
          if (item.rateClass) {
            const valStr = Number(item.rate).toLocaleString();
            grouped[key].rates[item.rateClass] = { val: valStr };
          }
        });

        const list = Object.values(grouped);
        setHistoryData(list);

        // Update rechartsData with real history
        const chartGrouped = {};
        res.data.forEach((item) => {
          const dateKey = item.effectiveDate;
          if (!chartGrouped[dateKey]) {
            chartGrouped[dateKey] = {
              rawDate: dateKey,
              date: dateKey,
            };
          }
          if (item.rateClass) {
            chartGrouped[dateKey][item.rateClass] = Number(item.rate) || 0;
          }
        });
        const chartList = Object.values(chartGrouped);
        chartList.sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
        
        chartList.forEach((item) => {
          try {
            const d = new Date(item.rawDate);
            if (!isNaN(d.getTime())) {
              item.date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            }
          } catch (e) {}
        });
        
        setRechartsData(chartList.slice(-10));
      }
    } catch (err) {
      console.warn("Could not fetch rate history from server", err);
    }
  };

  useEffect(() => {
    if (dbCode) {
      fetchRateClasses();
      fetchRateHistory();
    }

    const socket = io(apiBaseUrl);
    const handleRemoteUpdate = () => {
      fetchRateClasses();
      fetchRateHistory();
    };

    socket.on("RateClassUpdated", handleRemoteUpdate);
    socket.on("rateUpdated", handleRemoteUpdate);
    socket.on("rateHistoryUpdated", handleRemoteUpdate);

    return () => {
      socket.off("RateClassUpdated", handleRemoteUpdate);
      socket.off("rateUpdated", handleRemoteUpdate);
      socket.off("rateHistoryUpdated", handleRemoteUpdate);
      socket.disconnect();
    };
  }, [apiBaseUrl, dbCode]);

  const handleRateInputChange = (id, val) => {
    setRateClasses((prev) =>
      prev.map((item) => (item.id === id ? { ...item, newRate: val } : item))
    );
  };

  const setTodayDate = () => {
    setEffectiveDate(getCurrentDateStr());
  };

  // Save new rates into rate_history (leaving rateClass_registration intact!)
  const handleSave = async () => {
    if (permissions && permissions["rateSettings"]?.actions.Save === false) {
      alert("You do not have permission to save rate settings.");
      return;
    }

    // Simple alert confirmation
    const isConfirmed = window.confirm("Are you sure you want to save?");

    if (!isConfirmed) return;

    try {
      const payload = {
        effectiveDate: effectiveDate,
        effectiveTime: new Date().toLocaleTimeString('en-US', { hour12: false }),
        createdBy: "Admin",
        rates: rateClasses.map((rc) => ({
          rateClassId: rc.id,
          rateClass: rc.name,
          rate: rc.newRate !== "" ? rc.newRate : rc.currentRate,
        })),
      };

      const res = await axios.post(`${apiBaseUrl}/main/saveRateHistory/${dbCode}`, payload);
      if (res.status === 200 || res.data?.success) {
        alert("Rate History Saved Successfully!");
        // Refresh rate classes & history table
        fetchRateClasses();
        fetchRateHistory();
      } else {
        alert("Error saving rate history");
      }
    } catch (error) {
      console.error("Error saving rate history:", error);
      alert("Failed to save rate history: " + (error.response?.data?.message || error.message));
    }
  };

  const handleRefresh = () => {
    fetchRateClasses();
    fetchRateHistory();
    alert("Refreshed rate data!");
  };

  const handlePrint = () => {
    alert("Printing Rate Settings...");
  };

  // Export Rate History table to Excel sheet (.xlsx)
  const handleExportExcel = () => {
    if (!historyData || historyData.length === 0) {
      alert("No rate history data available to export.");
      return;
    }

    const exportRows = historyData.map((row) => {
      const rowObj = {
        "Date": row.date || "",
        "Time": row.time || "",
      };

      rateClasses.forEach((rc) => {
        const valStr = row.rates?.[rc.name]?.val || "-";
        rowObj[rc.name] = valStr;
      });

      return rowObj;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rate_History");
    XLSX.writeFile(workbook, `Rate_History_${getCurrentDateStr()}.xlsx`);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-3 sm:p-5 font-Inter select-none"
      style={{ zIndex: zIndex || 100, fontFamily: "Inter, sans-serif" }}
    >
      <div className="w-[1120px] max-w-[98vw] h-[530px] max-h-[85vh] bg-slate-50 rounded-[6px] shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        <style>{`
          .report-scroll::-webkit-scrollbar {
            width: 5px;
            height: 5px;
          }
          .report-scroll::-webkit-scrollbar-track {
            background: #f1f5f9;
          }
          .report-scroll::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .report-scroll::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}</style>
        {/* Window Header */}
        <WindowHeader
          title="Rate Settings"
          logo={ssLogo}
          onClose={onClose}
          className="bg-[#1e40af] text-white h-8 px-3 shrink-0"
          closeBtnClassName="h-5 w-5 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm"
        />

        {/* Action Toolbar */}
        <ActionToolbar
          size="small"
          buttonHeight="30px"
          iconSize={14}
          buttonClassName="!flex-row !gap-1.5 px-2.5"
          showSave={permissions?.["rateSettings"]?.actions?.Save !== false}
          onSave={handleSave}
          showPrint={true}
          onPrint={handlePrint}
          showClear={false}
          showFind={false}
          showEdit={false}
          showDelete={false}
          showNavigation={false}
          showExit={true}
          onExit={onClose}
          customButtons={[
            {
              icon: FiRefreshCw,
              text: "Refresh",
              onClick: handleRefresh,
            },
          ]}
        />

        {/* Main Workspace Area */}
        <div className="flex-1 p-2.5 sm:p-3 flex flex-col md:flex-row gap-3 overflow-hidden bg-slate-100/70 min-h-0">
          {/* ── LEFT PANEL: RATE UPDATE FORM ── */}
          <div className="w-full md:w-[310px] shrink-0 bg-white rounded-xl p-3 shadow-sm border border-slate-200/90 flex flex-col justify-between overflow-y-auto min-h-0">
            <div className="flex flex-col gap-2 flex-1 min-h-0">
              {/* Form Title & Date Box */}
              <div className="flex items-center justify-between pb-1.5 border-b border-slate-100 shrink-0">
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <FiSliders className="text-blue-600" />
                  Update Today's Rates
                </span>
                <button
                  type="button"
                  onClick={setTodayDate}
                  className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-0.5 rounded transition-colors"
                >
                  Set Today
                </button>
              </div>

              {/* Effective Date Input */}
              <div className="flex flex-col gap-0.5 shrink-0">
                <label className="text-[10.5px] font-semibold text-slate-500 font-Inter">
                  Effective Date
                </label>
                <div className="relative flex items-center">
                  <input
                    type="date"
                    min={minDate}
                    max={maxDate}
                    value={effectiveDate}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && !isDateInFy(val)) {
                        alert("Date incompatible with financial year");
                        return;
                      }
                      setEffectiveDate(val);
                    }}
                    className="w-full pl-3 pr-3 py-1 bg-slate-50 border border-slate-300 rounded-md text-xs font-bold text-slate-800 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all shadow-xs"
                  />
                </div>
              </div>

              {/* Rate Input Cards List */}
              <div className="flex flex-col gap-1 flex-1 min-h-0">
                <label className="text-[10.5px] font-semibold text-slate-500 flex items-center justify-between shrink-0 font-Inter">
                  <span>Rate Classes</span>
                  <span className="text-[9.5px] text-slate-400">
                    Values in ₹ / gram
                  </span>
                </label>

                <div className="flex flex-col gap-1.5 flex-1 min-h-0 overflow-y-auto pr-0.5">
                  {rateClasses.map((item) => {
                    const parsedVal = parseFloat(
                      item.newRate.replace(/,/g, "")
                    );
                    const diff =
                      !isNaN(parsedVal) && item.currentRate
                        ? parsedVal - item.currentRate
                        : 0;

                    return (
                      <div
                        key={item.id}
                        className="p-1.5 px-2 bg-slate-50 hover:bg-slate-100/80 rounded-lg border border-slate-200/80 transition-all flex flex-col gap-1"
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${item.badgeColor} flex items-center gap-1`}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: item.dotColor }}
                            ></span>
                            {item.name}
                          </span>
                          <span className="text-[10px] text-slate-500 font-medium">
                            Current:{" "}
                            <span className="font-bold text-slate-700">
                              ₹{item.currentRate.toLocaleString()}
                            </span>
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <div className="relative flex-1">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[11px] font-bold">
                              ₹
                            </span>
                            <input
                              type="text"
                              value={item.newRate}
                              onChange={(e) =>
                                handleRateInputChange(item.id, e.target.value)
                              }
                              className="w-full pl-5 pr-2 py-0.5 bg-white border border-slate-300 rounded-md text-right text-xs font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-xs"
                            />
                          </div>

                          {diff !== 0 && (
                            <span
                              className={`text-[9.5px] font-bold px-1 py-0.5 rounded shrink-0 ${
                                diff > 0
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              {diff > 0 ? `+₹${diff}` : `-₹${Math.abs(diff)}`}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Submit / Save Rates Button */}
              <button
                type="button"
                onClick={handleSave}
                className="w-full bg-[#1e40af] hover:bg-blue-800 active:scale-[0.99] text-white font-bold py-1.5 px-3 rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all text-xs cursor-pointer mt-0.5"
              >
                <FiSave className="w-3.5 h-3.5" />
                <span>Save All Rate Settings</span>
              </button>
            </div>

            {/* Last Updated Footer */}
            <div className="border-t border-slate-100 pt-1.5 mt-1 flex flex-col gap-0.5 text-[10.5px] text-slate-500">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <FiClock className="text-slate-400" />
                  Last Updated
                </span>
                <span className="font-bold text-slate-800">21/Jul/2026</span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span>Updated by Admin</span>
                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                  <FiCheckCircle className="w-2.5 h-2.5" />
                  Synced
                </span>
              </div>
            </div>
          </div>

          {/* ── RIGHT PANEL: ANALYTICS & HISTORY ── */}
          <div className="flex-1 min-w-0 bg-white rounded-xl p-4 shadow-sm border border-slate-200/90 flex flex-col gap-3 overflow-hidden">
            {/* Header Tabs Bar */}
            <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b border-slate-100 shrink-0">
              {/* Tab Switcher */}
              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => setActiveTab("history")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeTab === "history"
                      ? "bg-white text-blue-700 shadow-xs"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <FiClock className="w-3.5 h-3.5" />
                  <span>Rate History</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("trend")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                    activeTab === "trend"
                      ? "bg-white text-blue-700 shadow-xs"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <FiTrendingUp className="w-3.5 h-3.5" />
                  <span>Rate Movement Trend</span>
                </button>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2">
                {activeTab === "history" && (
                  <div className="relative flex items-center">
                    <FiSearch className="absolute left-2.5 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search date or rate..."
                      value={historySearchQuery}
                      onChange={(e) => setHistorySearchQuery(e.target.value)}
                      className="pl-7 pr-6 py-1 w-36 sm:w-48 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium text-slate-800 focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 shadow-xs transition-all placeholder-slate-400"
                    />
                    {historySearchQuery && (
                      <button
                        type="button"
                        onClick={() => setHistorySearchQuery("")}
                        className="absolute right-2 text-slate-400 hover:text-slate-600 text-xs font-bold cursor-pointer"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 shadow-xs cursor-pointer hover:bg-slate-100">
                  <FiCalendar className="w-3.5 h-3.5 text-slate-400" />
                  <span>{selectedMonth}</span>
                </div>

                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                >
                  <FiDownload className="w-3.5 h-3.5" />
                  <span>Export</span>
                </button>
              </div>
            </div>

            {/* TAB 1: RATE HISTORY DATA TABLE */}
            {activeTab === "history" && (
              <div className="flex-1 flex flex-col gap-3 min-h-0 overflow-hidden">
                {/* Highlights Stats Row (Dynamic Rate Class Cards with Live Price Change Badges) */}
                <div className="grid grid-cols-3 gap-3 shrink-0">
                  {rateClasses.slice(0, 3).map((rc) => {
                    const is916 = rc.name.toLowerCase().includes("916");
                    const is22k = rc.name.toLowerCase().includes("22k");
                    const displayVal = rc.newRate !== "" && rc.newRate !== undefined
                      ? (isNaN(rc.newRate) ? rc.newRate : Number(rc.newRate).toLocaleString())
                      : (rc.currentRate ? Number(rc.currentRate).toLocaleString() : "0");

                    const parsedVal = parseFloat(String(rc.newRate || "").replace(/,/g, ""));
                    const currentVal = parseFloat(rc.currentRate) || 0;
                    const diff = !isNaN(parsedVal) && currentVal ? parsedVal - currentVal : 0;

                    return (
                      <div
                        key={rc.id}
                        className={`p-2.5 rounded-xl border flex items-center justify-between ${
                          is916
                            ? "bg-amber-50/70 border-amber-200/80"
                            : is22k
                            ? "bg-orange-50/70 border-orange-200/80"
                            : "bg-slate-50 border-slate-200"
                        }`}
                      >
                        <div>
                          <div
                            className={`text-[10px] font-bold uppercase ${
                              is916
                                ? "text-amber-700"
                                : is22k
                                ? "text-orange-700"
                                : "text-slate-500"
                            }`}
                          >
                            {rc.name} Rate
                          </div>
                          <div
                            className={`text-base font-extrabold mt-0.5 ${
                              is916
                                ? "text-amber-900"
                                : is22k
                                ? "text-orange-900"
                                : "text-slate-800"
                            }`}
                          >
                            ₹ {displayVal}
                          </div>
                        </div>

                        {diff !== 0 ? (
                          <span
                            className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded border ${
                              diff > 0
                                ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                : "bg-rose-100 text-rose-800 border-rose-300"
                            }`}
                          >
                            {diff > 0 ? `+₹${diff.toLocaleString()}` : `-₹${Math.abs(diff).toLocaleString()}`}
                          </span>
                        ) : (
                          <span className="text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md">
                            Live
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Main Data Table */}
                <div className="flex-1 overflow-auto rounded-lg border border-slate-300 shadow-xs report-scroll">
                  <table className="min-w-full w-max text-left border-collapse text-xs font-Inter">
                    <thead className="bg-[#0f56d3] text-white sticky top-0 font-semibold z-20">
                      <tr className="h-8">
                        <th className="py-1.5 px-3 font-semibold text-xs border-r border-blue-400/40 text-center sticky left-0 bg-[#0f56d3] z-30 min-w-[130px] whitespace-nowrap">
                          Date & Time
                        </th>
                        {rateClasses.map((rc, idx) => (
                          <th
                            key={rc.id || idx}
                            className="py-1.5 px-3 font-semibold text-xs border-r border-blue-400/40 min-w-[140px] whitespace-nowrap"
                          >
                            <span
                              className="inline-block w-2 h-2 rounded-full mr-1.5"
                              style={{ backgroundColor: rc.dotColor || "#3b82f6" }}
                            ></span>
                            {rc.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-300 font-medium text-slate-800">
                      {historyData
                        .filter((row) => {
                          if (!historySearchQuery.trim()) return true;
                          const q = historySearchQuery.toLowerCase().trim();
                          const dateMatch = row.date?.toLowerCase().includes(q);
                          const timeMatch = row.time?.toLowerCase().includes(q);
                          const rateMatch = Object.values(row.rates || {}).some((valObj) =>
                            String(valObj?.val || "").toLowerCase().includes(q)
                          );
                          return dateMatch || timeMatch || rateMatch;
                        })
                        .map((row, idx) => {
                          const rowBg = idx % 2 === 0 ? "bg-[#f7f7f7]" : "bg-[#eeeeee]";
                          return (
                            <tr
                              key={idx}
                              className={`h-8 border-b border-gray-300 hover:bg-blue-50/40 transition-colors duration-150 ${rowBg}`}
                            >
                              <td className={`py-1 px-3 font-semibold text-[11px] text-slate-900 text-center border-r border-gray-200 sticky left-0 ${rowBg} z-10 min-w-[130px] whitespace-nowrap`}>
                                {row.date} {row.time ? <span className="text-[10px] text-slate-500 font-normal">({row.time})</span> : null}
                              </td>

                              {rateClasses.map((rc, rcIdx) => {
                                const cellVal = row.rates?.[rc.name]?.val;

                                // Calculate rate change compared to previous record
                                const curNum = parseFloat(String(cellVal || "").replace(/,/g, ""));
                                const prevRow = historyData[idx + 1];
                                const prevCellVal = prevRow?.rates?.[rc.name]?.val;
                                const prevNum = parseFloat(String(prevCellVal || "").replace(/,/g, ""));
                                const rowDiff = !isNaN(curNum) && !isNaN(prevNum) && prevNum > 0 ? curNum - prevNum : 0;

                                return (
                                  <td
                                    key={rc.id || rcIdx}
                                    className="py-1 px-3 text-[11px] border-r border-gray-200 min-w-[140px] whitespace-nowrap"
                                  >
                                    <div className="flex items-center justify-between gap-1">
                                      <span className="font-semibold text-slate-900">
                                        ₹ {cellVal || "-"}
                                      </span>

                                      {rowDiff !== 0 && !isNaN(rowDiff) && (
                                        <span
                                          className={`inline-flex items-center text-[9.5px] font-bold px-1.5 py-0.5 rounded border ${
                                            rowDiff > 0
                                              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                                              : "text-rose-700 bg-rose-50 border-rose-200"
                                          }`}
                                        >
                                          {rowDiff > 0 ? `+₹${rowDiff.toLocaleString()}` : `-₹${Math.abs(rowDiff).toLocaleString()}`}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 2: RATE TREND RECHARTS ANALYTICS */}
            {activeTab === "trend" && (
              <div className="flex-1 flex flex-col gap-2.5 min-h-0">
                <div className="flex items-center justify-between flex-wrap gap-2 shrink-0">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-slate-500">
                      Rate movement analytics over dates
                    </span>

                    {/* Chart Type Toggle Button */}
                    <div className="flex items-center gap-0.5 bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-[10.5px] font-semibold">
                      <button
                        type="button"
                        onClick={() => setChartType("bar")}
                        className={`px-2 py-0.5 rounded transition-all flex items-center gap-1 cursor-pointer ${
                          chartType === "bar"
                            ? "bg-white text-blue-700 shadow-xs font-bold"
                            : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        <FiBarChart2 className="w-3 h-3" />
                        <span>Bar</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setChartType("line")}
                        className={`px-2 py-0.5 rounded transition-all flex items-center gap-1 cursor-pointer ${
                          chartType === "line"
                            ? "bg-white text-blue-700 shadow-xs font-bold"
                            : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        <FiTrendingUp className="w-3 h-3" />
                        <span>Line</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 bg-slate-50/70 rounded-xl border border-slate-200 p-2.5 relative min-h-0">
                  <ResponsiveContainer width="100%" height="100%">
                    {chartType === "bar" ? (
                      <BarChart
                        data={rechartsData}
                        margin={{ top: 10, right: 15, left: -15, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11, fill: "#64748b" }}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: "#64748b" }}
                          domain={[0, 11000]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                            fontSize: "11px",
                            fontWeight: "bold",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          }}
                          formatter={(value) => [
                            `₹ ${value.toLocaleString()}`,
                            "",
                          ]}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
                        />
                        {rateClasses.map((rc) => (
                          <Bar
                            key={rc.id}
                            dataKey={rc.name}
                            fill={rc.dotColor || "#3b82f6"}
                            radius={[4, 4, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    ) : (
                      <LineChart
                        data={rechartsData}
                        margin={{ top: 10, right: 15, left: -15, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 11, fill: "#64748b" }}
                        />
                        <YAxis
                          tick={{ fontSize: 11, fill: "#64748b" }}
                          domain={[0, 11000]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            border: "1px solid #cbd5e1",
                            fontSize: "11px",
                            fontWeight: "bold",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          }}
                          formatter={(value) => [
                            `₹ ${value.toLocaleString()}`,
                            "",
                          ]}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
                        />
                        {rateClasses.map((rc) => (
                          <Line
                            key={rc.id}
                            type="monotone"
                            dataKey={rc.name}
                            stroke={rc.dotColor || "#3b82f6"}
                            strokeWidth={2.5}
                            dot={{ r: 3.5 }}
                            activeDot={{ r: 5 }}
                          />
                        ))}
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateSettings;
